import React, { useState, useCallback } from 'react'
import styled from 'styled-components'

import ArchiveDeviceModal from 'pages/shared/ArchivedDevices/ArchiveDeviceModal'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'

import { Panel } from 'components/Panel'

import { useSessionContext } from 'hooks/SessionContext'

import Api from 'services/api'

import { longDateTime } from 'helpers/formatters'
import { ButtonContainer, screenSize } from 'styles/shared'

import { Device } from 'types/Device'
import { ArchivedDevice } from 'types/ArchivedDevice'

import { useConfirmDialog } from 'hooks/useDialog'

import { CONFIRMATION_DIALOG_MESSAGES } from 'helpers/confirmationDialogMessages'

type props = {
  device: Device
  refreshPageCallback?: () => void | null | undefined
}

const AssignedSystemTime = ({ device, refreshPageCallback }: props) => {
  const [deleteInProgress, setDeleteInProgress] = useState(false)
  const [showArchiveDeviceModal, setShowArchiveDeviceModal] = useState(false)
  const { session } = useSessionContext()
  const { confirm } = useConfirmDialog()
  const componentType = 'System'

  const onDeleteSystem = useCallback(
    async (deviceId: string) => {
      const isConfirmed = await confirm({
        title: CONFIRMATION_DIALOG_MESSAGES.DELETE.TITLE(componentType),
        description:
          CONFIRMATION_DIALOG_MESSAGES.DELETE.DESCRIPTION(componentType),
        confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.DELETE
      })

      if (isConfirmed) {
        setDeleteInProgress(true)

        await Api.Device.delete(deviceId)
          .then(() => {
            refreshPageCallback && refreshPageCallback()
          })
          .finally(() => setDeleteInProgress(false))
      }
    },
    [refreshPageCallback, confirm]
  )

  const onCreateCallback = async (archivedDevice: Omit<ArchivedDevice, 'id' | 'createdAt'>) => {
    await Api.ArchivedDevices.create(archivedDevice.dealerId, archivedDevice)
      .then(() => {
        refreshPageCallback && refreshPageCallback()
      })
  }

  return (
    <S.Panel>
      {!session.isFsr && device && (
        <S.AssignedSystemTime>
          <span>Dealer assigned this system to the customer in Diagnostics on: {longDateTime(device.createdAt)}</span>

          <S.ButtonContainer>
            {(!device.registered || !device.optedIn) && <Button decoration="danger" text="DELETE SYSTEM" enabled={!deleteInProgress} onPress={() => onDeleteSystem(device.deviceId)} /> }
            {(device.registered && device.optedIn) && <Button decoration="danger-secondary" text="ARCHIVE SYSTEM" onPress={() => setShowArchiveDeviceModal(true)} />}
          </S.ButtonContainer>
        </S.AssignedSystemTime>
      )}

      {device && (
        <ArchiveDeviceModal
          isVisible={showArchiveDeviceModal}
          closeCallback={() => setShowArchiveDeviceModal(false)}
          actionType="create"
          auid={device.deviceId}
          onUpdateCallback={(reason) => onCreateCallback({ auid: device.deviceId, dealerId: device.dealerId, registered: device.registered, reason })}
        />
      )}
    </S.Panel>
  )
}

export default AssignedSystemTime

const S = {
  Panel: styled(Panel)`
    border-bottom: 0;
    padding: 5px 0px;

    @media ${screenSize.small} {
      padding: 5px 10px;
    }
  `,
  AssignedSystemTime: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media ${screenSize.small} {
      justify-content: center;
      flex-direction: column;
    }
  `,
  ButtonContainer: styled(ButtonContainer)`
    margin: 0px;
    padding: 0px;
    border: none;

    & > button {
      margin: 0 !important;
      height: 34px !important;
      min-width: 64px !important;
   }
  `
}
